import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, ShieldCheck, ArrowRight, Loader2, AlertTriangle, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Heading, Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { useAuth } from '../../context/AuthContext';

/**
 * Passwordless "verify email → start test" gate.
 *
 * Shown when a logged-out candidate opens a test link (/instructions?test_id=…).
 * Reuses the platform's existing OTP endpoints, then exchanges the verified
 * email for a scoped test session via /api/test-access/start (which authorizes
 * the email against the test's invite list). No account/password creation.
 */
export default function StartTestVerify({ testId }) {
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const navigate = useNavigate();
  const { loginUser } = useAuth();

  const cleanEmail = () => email.trim().toLowerCase();
  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const sendCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setInfo('');
    const em = cleanEmail();
    if (!isEmail(em)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/send_otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: em }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not send the code. Please try again.');
      }
      setStep('otp');
      setInfo(`A 6-digit verification code has been sent to ${em}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyAndStart = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    const em = cleanEmail();
    const code = otp.trim();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      // 1) Verify the OTP — stamps session["email_verified"] on the server.
      const vres = await fetch('/api/verify_otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: em, otp: code }),
      });
      const vdata = await vres.json().catch(() => ({}));
      if (!vres.ok || !vdata.verified) {
        throw new Error(vdata.error || 'That code is invalid or has expired.');
      }

      // 2) Exchange the verified email for a scoped test session. Authorization
      //    (is this email invited to this test?) happens server-side here.
      const sres = await fetch('/api/test-access/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_id: testId }),
      });
      const sdata = await sres.json().catch(() => ({}));
      if (!sres.ok || !sdata.ok) {
        throw new Error(sdata.error || 'Could not start the test for this email.');
      }

      // 3) Persist auth (reload-safe) and hard-navigate into the test so it
      //    loads with a clean, established session (proctoring, media, etc.).
      try { loginUser(em); } catch (_) { /* context write is best-effort */ }
      localStorage.setItem('isUserAuthenticated', 'true');
      localStorage.setItem('user_email', em);
      localStorage.setItem('loginTimestamp', Date.now().toString());
      window.location.assign(
        sdata.redirect || `/instructions?test_id=${encodeURIComponent(testId)}`
      );
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background text-foreground flex flex-col justify-center items-center relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Logo - top left */}
      <div className="absolute top-6 left-6 md:top-8 md:left-8 z-10">
        <Link to="/" className="transition-opacity hover:opacity-80">
          <img src="/Logo.png" alt="Logo" className="h-8 md:h-10 w-auto object-contain drop-shadow-sm" />
        </Link>
      </div>

      <div className="flex justify-center items-center gap-12 max-w-6xl w-full flex-wrap px-6 py-12 md:flex-nowrap relative z-10">
        <Card className="w-full max-w-md bg-white border border-border/50 shadow-xl shadow-primary/5">
          <CardContent className="p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <Heading level="h1" className="text-2xl font-bold tracking-tight">
              Start your assessment
            </Heading>
            <Text className="mt-2 text-sm text-muted-foreground">
              {step === 'email'
                ? 'Please confirm the email address your invitation was sent to. We will email you a 6-digit verification code.'
                : 'Enter the 6-digit verification code we emailed you.'}
            </Text>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <Text size="sm" className="text-destructive">{error}</Text>
            </div>
          )}
          {info && !error && (
            <div className="mb-4 rounded-md bg-primary/5 border border-primary/20 p-3">
              <Text size="sm" className="text-muted-foreground">{info}</Text>
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={sendCode} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="verify-email">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
                  <Input
                    id="verify-email"
                    type="email"
                    autoFocus
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 h-10"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? 'Sending…' : 'Send code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyAndStart} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="verify-otp">6-digit code</Label>
                <Input
                  id="verify-otp"
                  type="text"
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="••••••"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full h-11 text-center text-lg tracking-[0.4em] font-semibold"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {loading ? 'Verifying…' : 'Verify & start test'}
              </Button>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setStep('email'); setOtp(''); setError(''); setInfo(''); }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Change email
                </button>
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={loading}
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <RotateCcw className="w-3 h-3" /> Resend code
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-border text-center">
            <Text size="sm" className="text-muted-foreground">
              Prefer to sign in with a password?{' '}
              <button
                type="button"
                onClick={() => {
                  try {
                    localStorage.setItem(
                      'intendedDestination',
                      `/instructions?test_id=${testId}`
                    );
                  } catch (_) { /* ignore storage errors */ }
                  navigate('/login_user');
                }}
                className="text-primary font-medium hover:underline"
              >
                Log in
              </button>
            </Text>
          </div>
          </CardContent>
        </Card>

        {/* Hero image — large screens only */}
        <div className="hidden lg:block flex-shrink-0">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl blur-2xl" />
            <img
              className="relative w-full max-w-[480px] aspect-[0.72] object-cover rounded-2xl shadow-2xl"
              loading="lazy"
              src="./abcd.png"
              alt="Assessment illustration"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
